import { useCallback, useMemo } from "react";
import { supabase } from "./supabase";
import { useCachedQuery } from "./useCachedQuery";

// useCommunityRecipes — the community recipe leaderboard read path.
//
// REPLACES the failed Courses feature. Three time windows (all-time,
// monthly, weekly) over the public.community_leaderboard view added in
// migration 0236. Reads are 24h-TTL cached per the CLAUDE.md egress
// discipline; the leaderboard is slow-changing and no realtime sub is
// warranted at MVP scale (~< 1000 published recipes expected near-term).
//
// Three public surfaces here:
//
//   1. useCommunityLeaderboard({ window }) — the ranked top-100 for the
//      chosen window. Powers the Community tab.
//
//   2. publishToCommunity(recipeId) / unpublishFromCommunity(recipeId)
//      — author-only opt-in. Flips user_recipes.community_published
//      and stamps the timestamp. Existing user_recipes update RLS
//      gates by user_id so non-authors get denied.
//
//   3. findCommunityMatches(prompt, aliases, opts) — lemma-token
//      matcher used by AIRecipe's pre-gen dedupe. Loads ALL community
//      candidates once (same 24h cache), then matches in-memory so
//      every keystroke isn't a network call. Returns ranked matches
//      with a full diagnostic block (per the beta-transparency rule).
//
// Egress shape per CLAUDE.md:
//   - Single SELECT on the leaderboard view, capped at 100 rows
//   - Cached 24h, mutates write through to localStorage on publish
//   - No realtime subscription
//
// Cost-savings goal: the pre-gen matcher exists specifically to dodge
// Sonnet generation costs when an existing community recipe covers the
// prompt. Every hit is roughly $0.13 saved (skipped Sonnet sketch +
// final). Diagnostic dump lets us debug false negatives in TestFlight.

// v2: leaderboard rows gained author_name/avatar + distinct_cookers and
// the score/cook-attribution changed (author cooks now count). Bump so
// stale v1 rows (no author, old scores) are discarded on first load.
const CACHE_NS = "communityRecipes.v2";
const TTL_24H  = 24 * 60 * 60 * 1000;
const TOP_LIMIT = 100;

// English stopwords stripped from BOTH prompt and candidate titles
// during the token-match. Kept tiny — over-aggressive stopword removal
// kills legit signal ("french toast" → "toast" loses cuisine).
const STOPWORDS = new Set(["a", "an", "the", "with", "and", "of", "in", "on"]);

function tokenize(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, " ")
    .split(/\s+/)
    .filter((t) => t && !STOPWORDS.has(t));
}

// useCommunityLeaderboard — the ranked top-N for the chosen window.
// Window is the suffix on the score column; the view emits all three
// so we sort client-side by the active one (cheap, full set is small).
export function useCommunityLeaderboard({ window = "alltime" } = {}) {
  const safeWindow = ["alltime", "monthly", "weekly"].includes(window) ? window : "alltime";
  const { data, loading, refresh, mutate } = useCachedQuery({
    cacheKey: `${CACHE_NS}.leaderboard`,
    ttlMs:    TTL_24H,
    enabled:  true,
    defaultValue: [],
    fetcher: async () => {
      const { data: rows, error } = await supabase
        .from("community_leaderboard")
        .select("*")
        .order("score_alltime", { ascending: false })
        .limit(TOP_LIMIT);
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("[community] leaderboard fetch failed:", error.message);
        return [];
      }
      return rows || [];
    },
  });

  // Sort the cached rowset by the active window's score on read. Avoids
  // refetching when the user toggles windows; the rows already carry
  // all three scores.
  const sorted = useMemo(() => {
    const list = Array.isArray(data) ? [...data] : [];
    const scoreKey = `score_${safeWindow}`;
    const cookerKey = safeWindow === "weekly"
      ? "distinct_cookers_weekly"
      : safeWindow === "monthly" ? "distinct_cookers_monthly" : "distinct_cookers";
    // Deterministic order: score, then distinct cookers (reach), then
    // newest published. Without the tiebreaks every not-yet-cooked
    // recipe scores 0 and the list shuffled on each load.
    list.sort((a, b) => {
      const s = (Number(b?.[scoreKey]) || 0) - (Number(a?.[scoreKey]) || 0);
      if (s) return s;
      const c = (Number(b?.[cookerKey]) || 0) - (Number(a?.[cookerKey]) || 0);
      if (c) return c;
      return new Date(b?.community_published_at || 0) - new Date(a?.community_published_at || 0);
    });
    return list;
  }, [data, safeWindow]);

  return { rows: sorted, loading, refresh, mutate };
}

// publishToCommunity / unpublishFromCommunity — author opt-in toggles.
// Direct table update; RLS (user_recipes update policy) enforces that
// only the recipe's author can flip the flag. Returns { ok, error }.
export async function publishToCommunity(recipeId) {
  if (!recipeId) return { ok: false, error: "recipeId required" };
  const { error } = await supabase
    .from("user_recipes")
    .update({
      community_published:    true,
      community_published_at: new Date().toISOString(),
    })
    .eq("id", recipeId);
  if (error) {
    console.warn("[community] publish failed:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

export async function unpublishFromCommunity(recipeId) {
  if (!recipeId) return { ok: false, error: "recipeId required" };
  const { error } = await supabase
    .from("user_recipes")
    .update({
      community_published:    false,
      community_published_at: null,
    })
    .eq("id", recipeId);
  if (error) {
    console.warn("[community] unpublish failed:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

// findCommunityMatches — synchronous lemma-token matcher run against
// the SAME leaderboard rowset the tab renders. No extra network call.
//
// Match rule: every prompt token must appear in (title tokens UNION
// canonical aliases tokens). Floor: cookCount >= minCooks AND
// avgRating >= minAvg. Top N returned, ranked by score_alltime.
//
// `aliases` is the array from classifyDishPrompt's DishContract — we
// expand the prompt's keyword bag by tokenizing each alias too, so
// "CCC" / "choco chip cookies" both expand into the same token set
// before matching against community recipe titles.
//
// Returns { matches, diagnostics }. matches = up to `limit` candidate
// rows. diagnostics = per-candidate why-it-matched-or-didn't summary
// (beta transparency).
export function findCommunityMatches(rows, promptText, aliases = [], opts = {}) {
  const minCooks = Number.isFinite(opts.minCooks) ? opts.minCooks : 3;
  const minAvg   = Number.isFinite(opts.minAvg)   ? opts.minAvg   : 1.5;
  const limit    = Number.isFinite(opts.limit)    ? opts.limit    : 3;

  const promptTokens = tokenize(promptText);
  const aliasTokens = Array.isArray(aliases)
    ? Array.from(new Set(aliases.flatMap((a) => tokenize(a))))
    : [];
  // The matcher's keyword bag is the union of prompt + alias tokens.
  // Every keyword in the bag must appear in the candidate's title
  // tokens to count as a match. Empty bag = no matches (don't suggest
  // random recipes on an empty prompt).
  const bag = new Set([...promptTokens, ...aliasTokens]);
  if (bag.size === 0) return { matches: [], diagnostics: [] };

  const diagnostics = [];
  const eligible = [];
  for (const row of rows || []) {
    const titleTokens = new Set(tokenize(row.title));
    const matchedTokens = [];
    const missingTokens = [];
    for (const tok of promptTokens) {
      if (titleTokens.has(tok)) matchedTokens.push(tok);
      else missingTokens.push(tok);
    }
    const tokenMatch    = missingTokens.length === 0 && promptTokens.length > 0;
    const cookCount     = Number(row.cook_count) || 0;
    const avgRating     = Number(row.avg_rating) || 0;
    const passesFloor   = cookCount >= minCooks && avgRating >= minAvg;
    const passes        = tokenMatch && passesFloor;
    diagnostics.push({
      recipeId:      row.recipe_id,
      title:         row.title,
      promptTokens,
      titleTokens:   Array.from(titleTokens),
      matchedTokens,
      missingTokens,
      cookCount,
      avgRating,
      tokenMatch,
      passesFloor,
      passes,
      score:         Number(row.score_alltime) || 0,
    });
    if (passes) eligible.push(row);
  }
  eligible.sort((a, b) => (Number(b.score_alltime) || 0) - (Number(a.score_alltime) || 0));
  return { matches: eligible.slice(0, limit), diagnostics };
}

// useCommunityDedupe — convenience hook for the pre-gen intercept.
// Pulls the same cached leaderboard rowset and exposes a `match` fn
// that callers invoke with the user's prompt + dish-contract aliases.
export function useCommunityDedupe() {
  const { rows, loading } = useCommunityLeaderboard({ window: "alltime" });
  const match = useCallback((promptText, aliases, opts) => {
    return findCommunityMatches(rows, promptText, aliases, opts);
  }, [rows]);
  return { match, loading, rowCount: rows.length };
}
